import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { Close, ContentCopy, PersonOutline, AdminPanelSettings, Visibility } from "@mui/icons-material";
import { PlatformIntegration, PlatformUser } from "types/interfaces/adminUsers";
import AppDialog from "../common/AppDialog";
import CustomScrollbars from "../CustomScrollbars";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useUpdateAdminUserMutation,
  useResetAdminUserPasswordMutation,
  useCreateAdminUserMutation,
  useArchiveAdminUserMutation,
  useGenerateAdminUserPasswordMutation,
  useUpdateAdminUserPermissionsMutation,
  useGetAdminUserFoldersQuery,
  useGetAssignableFoldersQuery,
  useGetAdminUserIntegrationsQuery,
  useGetAssignableIntegrationsQuery,
  useGrantFolderAccessMutation,
  useRevokeFolderAccessMutation,
  useGrantIntegrationAccessMutation,
  useRevokeIntegrationAccessMutation,
  useCreateAdminChatBotProfileMutation,
  useUpdateAdminChatBotProfileMutation,
  useGetAdminChatBotProfileQuery,
} from "features/adminUsers/adminUsers";
import type { AdminFolderAccess } from "types/interfaces/adminUsers";
import { useAppDispatch, useAppSelector } from "features/store";
import { api } from "features/api";
import { setClientView } from "features/clientView/clientViewSlice";
import { useNavigate } from "react-router-dom";
import {
  CREATE_MODE_DRAFT_KEY,
  type UserManagementDrafts,
} from "userManagement/platformDraftsStorage";
import UserManagementChatBotTab from "./UserManagementChatBotTab";
import UserManagementOwnedFoldersTab from "./UserManagementOwnedFoldersTab";
import UserManagementIntegrationsTab from "./UserManagementIntegrationsTab";
import {
  getUsernameValidationError,
  pickEmailServerError,
  pickUsernameServerError,
} from "utils/usernameValidation";

interface UserManagementPanelProps {
  user: PlatformUser | null;
  onClose: () => void;
  onUserUpdated: (user: PlatformUser) => void;
  onUserArchived?: () => void;
  onSavingChange?: (saving: boolean) => void;
  drafts: Record<number, UserManagementDrafts>;
  onDraftsChange: (
    updater: (prev: Record<number, UserManagementDrafts>) => Record<number, UserManagementDrafts>,
  ) => void;
}

interface ToastState {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  showWarning?: boolean;
}

export interface ConfirmAccessChangesSection {
  label: string;
  items?: string[];
  added?: string[];
  updated?: string[];
  removed?: string[];
  enabled?: string[];
  disabled?: string[];
}

interface SaveConfirmState {
  open: boolean;
  sections: ConfirmAccessChangesSection[];
  onConfirm: () => void;
}

type TabId = "profile" | "folders" | "integrations" | "permissions" | "chatbot";

const EMPTY_FOLDER_LIST: AdminFolderAccess[] = [];
const EMPTY_INTEGRATION_LIST: PlatformIntegration[] = [];

export type { UserManagementDrafts } from "userManagement/platformDraftsStorage";
export { CREATE_MODE_DRAFT_KEY } from "userManagement/platformDraftsStorage";

function rolesFromUser(u: PlatformUser | null) {
  return {
    is_data_storage_user: u?.profile.is_data_storage_user ?? false,
    is_integration_dashboard_user: u?.profile.is_integration_dashboard_user ?? false,
    is_ai_agent_user: u?.profile.is_ai_agent_user ?? false,
  };
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === "") return true;
  return EMAIL_REGEX.test(trimmed);
}

function getFolderDisplayName(folder: AdminFolderAccess): string {
  if (folder.folder_display != null && folder.folder_display !== "") {
    return folder.folder_display;
  }
  const isMigration = !folder.is_integration && !folder.is_sftp_folder;
  const ownerName = (folder.owner_first_name || "").trim();
  if (isMigration && ownerName) {
    return `${ownerName} - ${folder.folder}`;
  }
  return folder.folder;
}

function parseIntegrationDisplayName(name: string): string {
  const match = name.match(/^\[([^\]]+)\]\s*/);
  return match ? name.slice(match[0].length) : name;
}

export default function UserManagementPanel({
  user,
  onClose,
  onUserUpdated,
  onUserArchived,
  onSavingChange,
  drafts,
  onDraftsChange,
}: UserManagementPanelProps) {
  const isCreateMode = user === null;
  const currentUser = useAppSelector((state) => state.auth.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const currentPP = currentUser?.platform_profile ?? (currentUser as any)?.profile;
  const isSuperUser = Boolean(currentPP?.is_superuser);
  const isManager = Boolean(currentPP?.is_calibrate_manager);
  const canEdit = isSuperUser;
  const canUseClientView = isSuperUser || isManager;

  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = useState<TabId>("profile");
  const [initialPasswordForUser, setInitialPasswordForUser] = useState<{ userId: number; password: string; label: string } | null>(null);
  const [toast, setToast] = useState<ToastState>({ open: false, message: "", severity: "success" });
  const [confirm, setConfirm] = useState<ConfirmState>({
    open: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });
  const [saveConfirm, setSaveConfirm] = useState<SaveConfirmState>({
    open: false,
    sections: [],
    onConfirm: () => {},
  });
  const [isSavingAll, setIsSavingAll] = useState(false);

  const [firstName, setFirstName] = useState(user?.first_name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [username, setUsername] = useState(user?.username ?? "");
  const [usernameServerError, setUsernameServerError] = useState<string | null>(null);
  const [emailServerError, setEmailServerError] = useState<string | null>(null);
  const [roles, setRoles] = useState(() => rolesFromUser(user));
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(!user?.profile.is_pass_reset);

  useEffect(() => {
    setFirstName(user?.first_name ?? "");
    setEmail(user?.email ?? "");
    setUsername(user?.username ?? "");
    setUsernameServerError(null);
    setEmailServerError(null);
    setRoles((prev) => {
      if (user && drafts[user.id]?.profile?.roles) {
        return drafts[user.id]!.profile!.roles;
      }
      return rolesFromUser(user);
    });
    setRequiresPasswordChange(() => {
      if (user && drafts[user.id]?.profile?.requiresPasswordChange !== undefined) {
        return drafts[user.id]!.profile!.requiresPasswordChange;
      }
      return !user?.profile.is_pass_reset;
    });
    setTab("profile");
    setInitialPasswordForUser((prev) => {
      if (user == null) return null;
      if (prev != null && prev.userId !== user.id) return null;
      return prev;
    });
  }, [user]);

  const isEmailInvalid = (email ?? "").trim() !== "" && !isValidEmail(email ?? "");
  const isEmailFieldInvalid = isEmailInvalid || Boolean(emailServerError);
  const emailHelperText = isEmailInvalid
    ? "Enter a valid email address"
    : emailServerError ?? undefined;
  const usernameClientError = getUsernameValidationError(username);
  const isUsernameInvalid = Boolean(usernameClientError ?? usernameServerError);
  const usernameHelperText = usernameClientError ?? usernameServerError ?? undefined;

  const hasProfileChanges = isCreateMode
    ? (username.trim() !== "" && firstName.trim() !== "" && !isEmailInvalid && !usernameClientError)
    : user != null &&
      !isEmailInvalid &&
      !usernameClientError &&
      ((user.username ?? "").trim() !== username.trim() ||
        (user.first_name ?? "").trim() !== firstName.trim() ||
        (user.email ?? "").trim() !== (email ?? "").trim() ||
        user.profile.is_data_storage_user !== roles.is_data_storage_user ||
        user.profile.is_integration_dashboard_user !== roles.is_integration_dashboard_user ||
        user.profile.is_ai_agent_user !== roles.is_ai_agent_user ||
        (user.profile.is_pass_reset === true) !== !requiresPasswordChange);

  const [updateUser, { isLoading: isSaving }] = useUpdateAdminUserMutation();
  const [createUser, { isLoading: isCreating }] = useCreateAdminUserMutation();
  const [resetPassword, { isLoading: isResetting }] = useResetAdminUserPasswordMutation();
  const [archiveUser, { isLoading: isArchiving }] = useArchiveAdminUserMutation();
  const [generatePassword, { isLoading: isGenerating }] = useGenerateAdminUserPasswordMutation();
  const [updatePermissions] = useUpdateAdminUserPermissionsMutation();
  const [grantFolder] = useGrantFolderAccessMutation();
  const [revokeFolder] = useRevokeFolderAccessMutation();
  const [grantIntegration] = useGrantIntegrationAccessMutation();
  const [revokeIntegration] = useRevokeIntegrationAccessMutation();
  const [createChatBotProfile] = useCreateAdminChatBotProfileMutation();
  const [updateChatBotProfile] = useUpdateAdminChatBotProfileMutation();

  const draftKey = user?.id ?? CREATE_MODE_DRAFT_KEY;
  const onDraftsChangeRef = useRef(onDraftsChange);
  onDraftsChangeRef.current = onDraftsChange;
  const prevFolderAccessRef = useRef<Record<number, boolean>>({});
  const prevIntegrationAccessRef = useRef<Record<number, boolean>>({});
  const reconcileBaselineDraftKeyRef = useRef(draftKey);

  const { data: userFolders } = useGetAdminUserFoldersQuery(user?.id ?? 0, { skip: !user?.id });
  const { data: assignableFolders } = useGetAssignableFoldersQuery(undefined, { skip: !isCreateMode });
  const foldersList = useMemo(() => {
    const raw = (isCreateMode ? assignableFolders : userFolders) ?? EMPTY_FOLDER_LIST;
    return raw.filter((f) => !f.is_sftp_folder);
  }, [isCreateMode, assignableFolders, userFolders]);

  const { data: userIntegrationsData } = useGetAdminUserIntegrationsQuery(user?.id ?? 0, { skip: !user?.id });
  const { data: assignableIntegrationsData } = useGetAssignableIntegrationsQuery(undefined, { skip: !isCreateMode });
  const integrationsList = useMemo(
    () =>
      (isCreateMode ? assignableIntegrationsData?.integrations : userIntegrationsData?.integrations) ??
      EMPTY_INTEGRATION_LIST,
    [isCreateMode, assignableIntegrationsData?.integrations, userIntegrationsData?.integrations],
  );

  const { data: chatbotProfile, isSuccess: chatbotProfileLoaded } = useGetAdminChatBotProfileQuery(user?.id ?? 0, {
    skip: !user?.id,
  });
  const chatbotProfileExists = chatbotProfileLoaded && !!chatbotProfile;

  const showToast = (message: string, severity: "success" | "error") => {
    setToast({ open: true, message, severity });
  };

  const effectiveIsDataStorage = roles.is_data_storage_user;
  const effectiveIsIntegration = roles.is_integration_dashboard_user;
  const effectiveIsAiAgent = roles.is_ai_agent_user;

  const folderDraft = drafts[draftKey]?.folders;
  const integrationDraft = drafts[draftKey]?.integrations;
  const chatbotDraft = drafts[draftKey]?.chatbot;
  const permissionDraft = drafts[draftKey]?.permissions;

  useEffect(() => {
    if (reconcileBaselineDraftKeyRef.current !== draftKey) {
      reconcileBaselineDraftKeyRef.current = draftKey;
      prevFolderAccessRef.current = {};
      prevIntegrationAccessRef.current = {};
    }

    if (foldersList.length === 0 && integrationsList.length === 0) return;

    const serverFolders: Record<number, boolean> = {};
    for (const f of foldersList) {
      serverFolders[f.id] = f.has_access === true;
    }
    const serverInts: Record<number, boolean> = {};
    for (const i of integrationsList) {
      serverInts[i.id] = i.has_access;
    }

    const prevF = prevFolderAccessRef.current;
    const prevI = prevIntegrationAccessRef.current;
    const hadFolderBaseline = Object.keys(prevF).length > 0;
    const hadIntBaseline = Object.keys(prevI).length > 0;

    let nextFd = folderDraft ? { ...folderDraft } : undefined;
    let foldersPruned = false;
    if (nextFd) {
      for (const key of Object.keys(nextFd)) {
        const fid = Number(key);
        if (Number.isNaN(fid)) continue;
        if (!(fid in serverFolders)) continue;
        if (nextFd[fid] === serverFolders[fid]) {
          delete nextFd[fid];
          foldersPruned = true;
        }
      }
      if (Object.keys(nextFd).length === 0) nextFd = undefined;
    }

    let nextInd = integrationDraft ? { ...integrationDraft } : undefined;
    let intPruned = false;
    if (nextInd) {
      for (const key of Object.keys(nextInd)) {
        const iid = Number(key);
        if (Number.isNaN(iid)) continue;
        if (!(iid in serverInts)) continue;
        if (nextInd[iid] === serverInts[iid]) {
          delete nextInd[iid];
          intPruned = true;
        }
      }
      if (Object.keys(nextInd).length === 0) nextInd = undefined;
    }

    const conflictFolderNames: string[] = [];
    if (folderDraft) {
      for (const key of Object.keys(folderDraft)) {
        const fid = Number(key);
        if (Number.isNaN(fid)) continue;
        const curr = serverFolders[fid];
        if (curr === undefined) continue;
        const p = prevF[fid];
        const dval = folderDraft[fid];
        if (p !== undefined && p !== curr && dval !== curr) {
          const folder = foldersList.find((x) => x.id === fid);
          if (folder) conflictFolderNames.push(getFolderDisplayName(folder));
        }
      }
    }

    const conflictIntNames: string[] = [];
    if (integrationDraft) {
      for (const key of Object.keys(integrationDraft)) {
        const iid = Number(key);
        if (Number.isNaN(iid)) continue;
        const curr = serverInts[iid];
        if (curr === undefined) continue;
        const p = prevI[iid];
        const dval = integrationDraft[iid];
        if (p !== undefined && p !== curr && dval !== curr) {
          const row = integrationsList.find((x) => x.id === iid);
          if (row) conflictIntNames.push(parseIntegrationDisplayName(row.integration_name));
        }
      }
    }

    const folderMapsDiffer =
      JSON.stringify(nextFd ?? null) !== JSON.stringify(folderDraft ?? null);
    const intMapsDiffer =
      JSON.stringify(nextInd ?? null) !== JSON.stringify(integrationDraft ?? null);

    if (folderMapsDiffer || intMapsDiffer) {
      onDraftsChangeRef.current((prev) => {
        const userEntry = prev[draftKey];
        if (!userEntry) return prev;
        const nextEntry: UserManagementDrafts = { ...userEntry };
        if (folderMapsDiffer) {
          if (nextFd) nextEntry.folders = nextFd;
          else delete nextEntry.folders;
        }
        if (intMapsDiffer) {
          if (nextInd) nextEntry.integrations = nextInd;
          else delete nextEntry.integrations;
        }
        const stillHas =
          Boolean(nextEntry.profile) ||
          Boolean(nextEntry.chatbot) ||
          nextEntry.permissions != null ||
          (nextEntry.folders && Object.keys(nextEntry.folders).length > 0) ||
          (nextEntry.integrations && Object.keys(nextEntry.integrations).length > 0);
        if (!stillHas) {
          const { [draftKey]: _, ...restUsers } = prev;
          return restUsers;
        }
        return { ...prev, [draftKey]: nextEntry };
      });
    }

    const conflictParts = [...conflictFolderNames, ...conflictIntNames];
    if (conflictParts.length > 0) {
      const max = 3;
      const shown = conflictParts.slice(0, max);
      const more = conflictParts.length - shown.length;
      showToast(
        `Access was updated elsewhere; review pending changes (${shown.join("; ")}${
          more > 0 ? `; +${more} more` : ""
        }).`,
        "error",
      );
    } else if ((foldersPruned || intPruned) && (hadFolderBaseline || hadIntBaseline)) {
      showToast("Draft updated to match the latest access from the server.", "success");
    }

    prevFolderAccessRef.current = { ...serverFolders };
    prevIntegrationAccessRef.current = { ...serverInts };
  }, [draftKey, foldersList, integrationsList, folderDraft, integrationDraft]);

  const hasFolderChanges = foldersList.some(
    (f) => (folderDraft?.[f.id] ?? f.has_access === true) !== (f.has_access === true),
  );
  const hasIntegrationChanges = integrationsList.some(
    (i) => (integrationDraft?.[i.id] ?? i.has_access) !== i.has_access,
  );
  const hasChatbotChanges = chatbotDraft != null;
  const hasPermissionChanges = isCreateMode
    ? permissionDraft?.is_calibrate_manager === true
    : user != null &&
      !user.profile.is_superuser &&
      (permissionDraft?.is_calibrate_manager !== undefined && permissionDraft.is_calibrate_manager !== user.profile.is_calibrate_manager);

  const hasAnyChanges = hasProfileChanges || hasFolderChanges || hasIntegrationChanges || hasChatbotChanges || hasPermissionChanges;

  const handleTabChange = (_: unknown, value: TabId) => {
    if (value === "folders" && !isCreateMode && !effectiveIsDataStorage) {
      showToast("Enable Data Storage User role first.", "error");
      return;
    }
    if (value === "integrations" && !isCreateMode && !effectiveIsIntegration) {
      showToast("Enable Integration Dashboard User role first.", "error");
      return;
    }
    if (value === "chatbot" && !isCreateMode && !effectiveIsAiAgent) {
      showToast("Enable AI Agent User role first.", "error");
      return;
    }
    if (value === "permissions" && !isSuperUser) {
      showToast("Only Super Users can manage permissions.", "error");
      return;
    }
    setTab(value);
  };

  const openConfirm = (opts: { title: string; message: string; onConfirm: () => void; showWarning?: boolean }) => {
    setConfirm({ open: true, ...opts });
  };
  const closeConfirm = () => setConfirm((c) => ({ ...c, open: false }));

  const openSaveConfirm = (sections: ConfirmAccessChangesSection[], onConfirm: () => void) => {
    setSaveConfirm({ open: true, sections, onConfirm });
  };
  const closeSaveConfirm = () => setSaveConfirm((s) => ({ ...s, open: false }));

  const buildSectionsForConfirm = (): ConfirmAccessChangesSection[] => {
    const sections: ConfirmAccessChangesSection[] = [];

    if (hasProfileChanges) {
      if (isCreateMode) {
        const added: string[] = [];
        added.push("Username", "Client Name");
        if ((email ?? "").trim()) added.push("Email Address");
        const enabled: string[] = [];
        const disabled: string[] = [];
        if (roles.is_data_storage_user) enabled.push("Data Storage User");
        else disabled.push("Data Storage User");
        if (roles.is_integration_dashboard_user) enabled.push("Integration Dashboard User");
        else disabled.push("Integration Dashboard User");
        if (roles.is_ai_agent_user) enabled.push("AI Agent User");
        else disabled.push("AI Agent User");
        sections.push({ label: "PROFILE", added, enabled, disabled });
      } else if (user) {
        const updated: string[] = [];
        const enabled: string[] = [];
        const disabled: string[] = [];
        if ((user.username ?? "").trim() !== username.trim()) updated.push("Username");
        if ((user.first_name ?? "").trim() !== firstName.trim()) updated.push("Client Name");
        if ((user.email ?? "").trim() !== (email ?? "").trim()) updated.push("Email Address");
        if (user.profile.is_data_storage_user !== roles.is_data_storage_user) {
          (roles.is_data_storage_user ? enabled : disabled).push("Data Storage User");
        }
        if (user.profile.is_integration_dashboard_user !== roles.is_integration_dashboard_user) {
          (roles.is_integration_dashboard_user ? enabled : disabled).push("Integration Dashboard User");
        }
        if (user.profile.is_ai_agent_user !== roles.is_ai_agent_user) {
          (roles.is_ai_agent_user ? enabled : disabled).push("AI Agent User");
        }
        if ((user.profile.is_pass_reset === true) !== !requiresPasswordChange) {
          (requiresPasswordChange ? enabled : disabled).push("Require password change");
        }
        if (updated.length > 0 || enabled.length > 0 || disabled.length > 0) {
          sections.push({ label: "PROFILE", updated, enabled, disabled });
        }
      }
    }

    if (hasFolderChanges && foldersList.length > 0) {
      const added: string[] = [];
      const removed: string[] = [];
      for (const folder of foldersList) {
        const wanted = folderDraft?.[folder.id] ?? folder.has_access === true;
        const current = folder.has_access === true;
        if (wanted === current) continue;
        const name = getFolderDisplayName(folder);
        if (wanted) added.push(name);
        else removed.push(name);
      }
      if (added.length > 0 || removed.length > 0) {
        sections.push({ label: "FOLDERS", added, removed });
      }
    }

    if (hasIntegrationChanges && integrationsList.length > 0) {
      const added: string[] = [];
      const removed: string[] = [];
      for (const integration of integrationsList) {
        const wanted = integrationDraft?.[integration.id] ?? integration.has_access;
        if (wanted === integration.has_access) continue;
        const name = parseIntegrationDisplayName(integration.integration_name);
        if (wanted) added.push(name);
        else removed.push(name);
      }
      if (added.length > 0 || removed.length > 0) {
        sections.push({ label: "INTEGRATIONS", added, removed });
      }
    }

    if (hasChatbotChanges && chatbotDraft) {
      if (chatbotProfileExists) {
        const updated: string[] = [];
        if ((chatbotDraft.chat_id ?? "") !== (chatbotProfile?.chat_id ?? "")) updated.push("Chat ID");
        if ((chatbotDraft.api_key ?? "") !== (chatbotProfile?.api_key ?? "")) updated.push("API Key");
        if (updated.length > 0) sections.push({ label: "AI AGENT", updated });
      } else {
        const added: string[] = [];
        if (chatbotDraft.chat_id) added.push("Chat ID");
        if (chatbotDraft.api_key) added.push("API Key");
        if (added.length === 0) added.push("Chat ID", "API Key");
        sections.push({ label: "AI AGENT", added });
      }
    }

    if (hasPermissionChanges && user) {
      const wanted = permissionDraft?.is_calibrate_manager ?? user.profile.is_calibrate_manager;
      if (wanted) {
        sections.push({ label: "PERMISSIONS", enabled: ["Calibrate Manager"] });
      } else {
        sections.push({ label: "PERMISSIONS", disabled: ["Calibrate Manager"] });
      }
    } else if (hasPermissionChanges && isCreateMode && permissionDraft?.is_calibrate_manager) {
      sections.push({ label: "PERMISSIONS", enabled: ["Calibrate Manager"] });
    }

    return sections;
  };

  const executeUnifiedSave = async (targetUserId: number, skipProfile = false) => {
    setIsSavingAll(true);
    onSavingChange?.(true);
    try {
      if (hasProfileChanges && !skipProfile) {
        const updated = await updateUser({
          id: targetUserId,
          username,
          first_name: firstName,
          email,
          ...roles,
          is_pass_reset: !requiresPasswordChange,
        }).unwrap();
        onUserUpdated(updated);
        onDraftsChange((prev) => {
          const current = prev[targetUserId];
          if (!current?.profile) return prev;
          const { profile, ...restForUser } = current;
          const next = { ...prev };
          if (Object.keys(restForUser).length === 0) {
            const { [targetUserId]: _, ...restUsers } = next;
            return restUsers;
          }
          next[targetUserId] = restForUser as UserManagementDrafts;
          return next;
        });
      }

      if (hasFolderChanges) {
        for (const folder of foldersList) {
          const wanted = folderDraft?.[folder.id] ?? folder.has_access === true;
          const current = folder.has_access === true;
          if (wanted === current) continue;
          if (wanted) {
            await grantFolder({ userId: targetUserId, folderId: folder.id }).unwrap();
          } else {
            await revokeFolder({ userId: targetUserId, folderId: folder.id }).unwrap();
          }
        }
        onDraftsChange((prev) => {
          const u = prev[targetUserId];
          if (!u?.folders) return prev;
          const { folders: _, ...rest } = u;
          const next = { ...prev };
          if (Object.keys(rest).length === 0) {
            const { [targetUserId]: __, ...restUsers } = next;
            return restUsers;
          }
          next[targetUserId] = rest as UserManagementDrafts;
          return next;
        });
      }

      if (hasIntegrationChanges) {
        for (const integration of integrationsList) {
          const wanted = integrationDraft?.[integration.id] ?? integration.has_access;
          if (wanted === integration.has_access) continue;
          if (wanted) {
            await grantIntegration({ userId: targetUserId, matchId: integration.id }).unwrap();
          } else {
            await revokeIntegration({ userId: targetUserId, matchId: integration.id }).unwrap();
          }
        }
        onDraftsChange((prev) => {
          const u = prev[targetUserId];
          if (!u?.integrations) return prev;
          const { integrations: _, ...rest } = u;
          const next = { ...prev };
          if (Object.keys(rest).length === 0) {
            const { [targetUserId]: __, ...restUsers } = next;
            return restUsers;
          }
          next[targetUserId] = rest as UserManagementDrafts;
          return next;
        });
      }

      if (hasChatbotChanges && chatbotDraft) {
        if (!chatbotProfileExists) {
          await createChatBotProfile({
            userId: targetUserId,
            chat_id: chatbotDraft.chat_id || "pending",
            api_key: chatbotDraft.api_key,
          }).unwrap();
        } else {
          await updateChatBotProfile({
            userId: targetUserId,
            chat_id: chatbotDraft.chat_id ?? chatbotProfile!.chat_id,
            api_key: chatbotDraft.api_key,
          }).unwrap();
        }
        onDraftsChange((prev) => {
          const u = prev[targetUserId];
          if (!u?.chatbot) return prev;
          const { chatbot: _, ...rest } = u;
          const next = { ...prev };
          if (Object.keys(rest).length === 0) {
            const { [targetUserId]: __, ...restUsers } = next;
            return restUsers;
          }
          next[targetUserId] = rest as UserManagementDrafts;
          return next;
        });
      }

      if (hasPermissionChanges && permissionDraft) {
        const updated = await updatePermissions({ id: targetUserId, is_calibrate_manager: permissionDraft.is_calibrate_manager }).unwrap();
        onUserUpdated(updated);
        clearDraft(targetUserId, "permissions");
      }

      showToast(isCreateMode ? "User created successfully." : "User updated successfully.", "success");
    } catch (err: any) {
      const uMsg = pickUsernameServerError(err);
      if (uMsg) setUsernameServerError(uMsg);
      const eMsg = pickEmailServerError(err);
      if (eMsg) setEmailServerError(eMsg);
      const errors = err?.data?.errors;
      const msg = errors ? Object.values(errors).flat().join(" ") : "Failed to save.";
      showToast(msg, "error");
    } finally {
      setIsSavingAll(false);
      onSavingChange?.(false);
    }
  };

  const handleCreateClick = () => {
    const sections = buildSectionsForConfirm();
    if (sections.length === 0) {
      showToast("Please fill in required fields.", "error");
      return;
    }
    openSaveConfirm(sections, async () => {
      closeSaveConfirm();
      setIsSavingAll(true);
      onSavingChange?.(true);
      try {
        const created = await createUser({
          username,
          first_name: firstName,
          email,
          ...roles,
        }).unwrap();
        if (created.generated_password) {
          setInitialPasswordForUser({ userId: created.id, password: created.generated_password, label: "Initial Password" });
        }
        onUserUpdated(created);
        await executeUnifiedSave(created.id, true);
        onDraftsChange((prev) => {
          const { [CREATE_MODE_DRAFT_KEY]: _, ...rest } = prev;
          return rest;
        });
      } catch (err: any) {
        const uMsg = pickUsernameServerError(err);
        if (uMsg) setUsernameServerError(uMsg);
        const eMsg = pickEmailServerError(err);
        if (eMsg) setEmailServerError(eMsg);
        const errors = err?.data?.errors;
        const msg = errors ? Object.values(errors).flat().join(" ") : "Failed to create user.";
        showToast(msg, "error");
      } finally {
        setIsSavingAll(false);
        onSavingChange?.(false);
      }
    });
  };

  const handleResetPassword = () => {
    if (!user) return;
    openConfirm({
      title: "Reset Password",
      message: `Send a password reset email to ${user.email || user.username}?`,
      onConfirm: async () => {
        try {
          await resetPassword(user.id).unwrap();
          showToast("Password reset email sent.", "success");
        } catch {
          showToast("Failed to send reset email.", "error");
        }
      },
    });
  };

  const handleGeneratePassword = () => {
    if (!user) return;
    openConfirm({
      title: "Generate New Password",
      message: `Generate a new password for ${user.first_name || user.username}? The current password will be replaced immediately.`,
      showWarning: true,
      onConfirm: async () => {
        try {
          const result = await generatePassword(user.id).unwrap();
          setInitialPasswordForUser({
            userId: user.id,
            password: result.generated_password,
            label: "Generated Password",
          });
          const updated = await updateUser({
            id: user.id,
            is_pass_reset: false,
          }).unwrap();
          onUserUpdated(updated);
          setRequiresPasswordChange(true);
          showToast("Password generated.", "success");
        } catch {
          showToast("Failed to generate password.", "error");
        }
      },
    });
  };

  const handleArchive = () => {
    if (!user) return;
    const isArchived = user.profile.is_archived;
    openConfirm({
      title: isArchived ? "Un-Archive User" : "Confirm User Archive",
      message: isArchived
        ? `Restore ${user.first_name || user.username}? They will be able to log in again.`
        : "You are about to Archive this user. This will remove their ability to log in.",
      showWarning: !isArchived,
      onConfirm: async () => {
        try {
          await archiveUser(user.id).unwrap();
          showToast(isArchived ? "User un-archived." : "User archived.", "success");
          onUserArchived?.();
        } catch {
          showToast("Failed to update archive status.", "error");
        }
      },
    });
  };

  const updateDraft = (
    userId: number,
    key: keyof UserManagementDrafts,
    value: UserManagementDrafts[keyof UserManagementDrafts],
  ) => {
    onDraftsChange((prev) => ({ ...prev, [userId]: { ...prev[userId], [key]: value } }));
  };
  const clearDraft = (userId: number, key: keyof UserManagementDrafts) => {
    onDraftsChange((prev) => {
      const next = { ...prev };
      const u = next[userId];
      if (!u) return prev;
      const { [key]: _, ...rest } = u;
      if (Object.keys(rest).length === 0) {
        const { [userId]: __, ...restUsers } = next;
        return restUsers;
      }
      next[userId] = rest as UserManagementDrafts;
      return next;
    });
  };

  const syncProfileDraft = (nextRoles: typeof roles, nextRequiresPasswordChange: boolean) => {
    if (!user) return;
    const serverRoles = rolesFromUser(user);
    const serverRequiresPasswordChange = !user.profile.is_pass_reset;
    const isSame =
      serverRoles.is_data_storage_user === nextRoles.is_data_storage_user &&
      serverRoles.is_integration_dashboard_user === nextRoles.is_integration_dashboard_user &&
      serverRoles.is_ai_agent_user === nextRoles.is_ai_agent_user &&
      serverRequiresPasswordChange === nextRequiresPasswordChange;

    if (isSame) {
      clearDraft(user.id, "profile");
    } else {
      updateDraft(user.id, "profile", {
        roles: nextRoles,
        requiresPasswordChange: nextRequiresPasswordChange,
      });
    }
  };

  const fullName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username
    : "New User";

  const handleSaveAll = () => {
    if (!hasAnyChanges || !user) return;
    if (isEmailFieldInvalid || isUsernameInvalid) return;
    const sections = buildSectionsForConfirm();
    if (sections.length === 0) return;
    openSaveConfirm(sections, () => {
      closeSaveConfirm();
      executeUnifiedSave(user.id);
    });
  };

  return (
    <>
      <Box
        sx={(theme: Theme) => ({
          position: "relative",
          width: { xs: "100%", sm: 380, md: 420 },
          maxWidth: "100%",
          minWidth: 0,
          flexShrink: 0,
          borderLeft: { xs: "none", sm: `1px solid ${theme.palette.divider}` },
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          overflow: "hidden",
        })}
      >
        
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                bgcolor: "rgba(31,93,107,0.10)",
                display: "grid",
                placeItems: "center",
                fontWeight: 900,
                fontSize: 14,
                color: "primary.main",
              }}
            >
              {isCreateMode ? <PersonOutline fontSize="small" /> : fullName[0]?.toUpperCase()}
            </Box>
            <Box>
              <Box display="flex" alignItems="center" gap={0.75}>
                <Typography variant="subtitle2" fontWeight={800} lineHeight={1.2}>
                  {fullName}
                </Typography>
                {user?.profile.is_superuser && (
                  <AdminPanelSettings sx={{ fontSize: 18, color: "primary.main" }} titleAccess="Super Admin" />
                )}
                {user?.profile.is_archived && (
                  <Chip
                    label="Archived"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: "rgba(211,47,47,0.10)",
                      color: "error.dark",
                    }}
                  />
                )}
              </Box>
              {user && (
                <Typography variant="caption" color="text.secondary">
                  @{user.username}
                </Typography>
              )}
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            {!isCreateMode && canUseClientView && user && user.id !== currentUser?.id && !user.profile.is_archived && (
              <Tooltip title="View the platform as this client would see it.">
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<Visibility fontSize="small" />}
                  onClick={() => {
                    const displayName = [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username;
                    dispatch(
                      setClientView({
                        userId: user.id,
                        displayName,
                        isChatBotAllowed: user.profile.is_ai_agent_user,
                        isIntegrationUser: user.profile.is_integration_dashboard_user,
                        isCalibrateAdmin: user.profile.is_calibrate_manager,
                      })
                    );
                    dispatch(api.util.resetApiState());
                    navigate(`/client-view/${user.id}`);
                  }}
                  sx={{ textTransform: "none", fontWeight: 600, fontSize: 11, px: 1, minWidth: "auto", borderRadius: 1.5 }}
                >
                  Client View
                </Button>
              </Tooltip>
            )}
            <IconButton size="small" onClick={onClose}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        
        <Box
          sx={{
            minWidth: 0,
            borderBottom: "1px solid",
            borderColor: "divider",
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ minHeight: 44 }}
            TabIndicatorProps={{ style: { height: 2 } }}
          >
            <Tab value="profile" label="Profile" sx={{ minHeight: 44, fontSize: 13, textTransform: "none", fontWeight: 700, minWidth: "auto", px: 1.5 }} />
            <Tab
              value="folders"
              label="Folders"
              disabled={!effectiveIsDataStorage}
              sx={{ minHeight: 44, fontSize: 13, textTransform: "none", fontWeight: 700, minWidth: "auto", px: 1.5 }}
            />
            <Tab
              value="integrations"
              label={isNarrow ? "Integr." : "Integrations"}
              disabled={!effectiveIsIntegration}
              sx={{ minHeight: 44, fontSize: 13, textTransform: "none", minWidth: "auto", px: 1.5 }}
            />
            <Tab
              value="chatbot"
              label={isNarrow ? "AI" : "AI Agent"}
              disabled={!effectiveIsAiAgent}
              sx={{ minHeight: 44, fontSize: 13, textTransform: "none", minWidth: "auto", px: 1.5 }}
            />
            {isSuperUser && (
              <Tab
                value="permissions"
                label={isNarrow ? "Perms" : "Permissions"}
                sx={{ minHeight: 44, fontSize: 13, textTransform: "none", minWidth: "auto", px: 1.5 }}
              />
            )}
          </Tabs>
        </Box>

        

        {tab === "folders" && (user || isCreateMode) && (
          <UserManagementOwnedFoldersTab
            userId={user?.id ?? null}
            isCreateMode={isCreateMode}
            isDataStorageUser={effectiveIsDataStorage}
            canEdit={canEdit}
            isCalibrateManager={isManager && !isSuperUser}
            folderDraft={drafts[user?.id ?? CREATE_MODE_DRAFT_KEY]?.folders}
            onFolderDraftChange={(folderId, hasAccess, serverHasAccess) =>
              onDraftsChange((prev) => {
                const draftKey = user?.id ?? CREATE_MODE_DRAFT_KEY;
                const current = prev[draftKey]?.folders ?? {};
                if (hasAccess === serverHasAccess) {
                  const { [folderId]: _, ...rest } = current;
                  if (Object.keys(rest).length === 0) {
                    const { folders: __, ...userRest } = prev[draftKey] ?? {};
                    if (Object.keys(userRest).length === 0) {
                      const { [draftKey]: ___, ...usersRest } = prev;
                      return usersRest;
                    }
                    return { ...prev, [draftKey]: userRest as UserManagementDrafts };
                  }
                  return { ...prev, [draftKey]: { ...prev[draftKey], folders: rest } };
                }
                return {
                  ...prev,
                  [draftKey]: { ...prev[draftKey], folders: { ...current, [folderId]: hasAccess } },
                };
              })
            }
            onSaveSuccess={showToast}
            onSaveComplete={() => clearDraft(user?.id ?? CREATE_MODE_DRAFT_KEY, "folders")}
            onSaveAll={handleSaveAll}
            onCreateUser={isCreateMode ? handleCreateClick : undefined}
            createUserDisabled={!canEdit || isCreating || isSavingAll || !username.trim() || !firstName.trim() || isEmailFieldInvalid || isUsernameInvalid}
            hasAnyChanges={hasAnyChanges}
            isSaving={isSavingAll}
          />
        )}

        {tab === "integrations" && (user || isCreateMode) && (
          <UserManagementIntegrationsTab
            userId={user?.id ?? null}
            isCreateMode={isCreateMode}
            isIntegrationDashboardUser={effectiveIsIntegration}
            canEdit={canEdit}
            isCalibrateManager={isManager && !isSuperUser}
            integrationDraft={drafts[user?.id ?? CREATE_MODE_DRAFT_KEY]?.integrations}
            onIntegrationDraftChange={(matchId, hasAccess, serverHasAccess) =>
              onDraftsChange((prev) => {
                const draftKey = user?.id ?? CREATE_MODE_DRAFT_KEY;
                const current = prev[draftKey]?.integrations ?? {};
                if (hasAccess === serverHasAccess) {
                  const { [matchId]: _, ...rest } = current;
                  if (Object.keys(rest).length === 0) {
                    const { integrations: __, ...userRest } = prev[draftKey] ?? {};
                    if (Object.keys(userRest).length === 0) {
                      const { [draftKey]: ___, ...usersRest } = prev;
                      return usersRest;
                    }
                    return { ...prev, [draftKey]: userRest as UserManagementDrafts };
                  }
                  return { ...prev, [draftKey]: { ...prev[draftKey], integrations: rest } };
                }
                return {
                  ...prev,
                  [draftKey]: { ...prev[draftKey], integrations: { ...current, [matchId]: hasAccess } },
                };
              })
            }
            onSuccess={showToast}
            onSaveComplete={() => clearDraft(user?.id ?? CREATE_MODE_DRAFT_KEY, "integrations")}
            onSaveAll={handleSaveAll}
            onCreateUser={isCreateMode ? handleCreateClick : undefined}
            createUserDisabled={!canEdit || isCreating || isSavingAll || !username.trim() || !firstName.trim() || isEmailFieldInvalid || isUsernameInvalid}
            hasAnyChanges={hasAnyChanges}
            isSaving={isSavingAll}
          />
        )}

        {tab === "chatbot" && (user || isCreateMode) && (
          <UserManagementChatBotTab
            userId={user?.id ?? null}
            isCreateMode={isCreateMode}
            canEdit={canEdit}
            isCalibrateManager={isManager && !isSuperUser}
            chatbotDraft={drafts[user?.id ?? CREATE_MODE_DRAFT_KEY]?.chatbot}
            onChatbotDraftChange={(data, serverProfile) =>
              onDraftsChange((prev) => {
                const draftKey = user?.id ?? CREATE_MODE_DRAFT_KEY;
                const same =
                  serverProfile != null &&
                  (data.chat_id ?? "") === (serverProfile.chat_id ?? "") &&
                  (data.api_key ?? "") === (serverProfile.api_key ?? "");
                if (same) {
                  const { chatbot: __, ...userRest } = prev[draftKey] ?? {};
                  if (Object.keys(userRest).length === 0) {
                    const { [draftKey]: ___, ...usersRest } = prev;
                    return usersRest;
                  }
                  return { ...prev, [draftKey]: userRest as UserManagementDrafts };
                }
                return { ...prev, [draftKey]: { ...prev[draftKey], chatbot: data } };
              })
            }
            onSuccess={showToast}
            onSaveComplete={() => clearDraft(user?.id ?? CREATE_MODE_DRAFT_KEY, "chatbot")}
            onSaveAll={handleSaveAll}
            onCreateUser={isCreateMode ? handleCreateClick : undefined}
            createUserDisabled={!canEdit || isCreating || isSavingAll || !username.trim() || !firstName.trim() || isEmailFieldInvalid || isUsernameInvalid}
            hasAnyChanges={hasAnyChanges}
            isSaving={isSavingAll}
          />
        )}

        {tab === "permissions" && (user || isCreateMode) && isSuperUser && (
          <>
            <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
              <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                {user?.profile.is_superuser && currentUser?.id === user?.id ? (
                  <Typography variant="body2" color="text.secondary">
                    You have full access to all permissions as a Super Admin.
                  </Typography>
                ) : (
                  <>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={700}
                      textTransform="uppercase"
                      letterSpacing={0.6}
                      display="block"
                      mb={0.75}
                    >
                      Manager Access
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="space-between" py={0.4}>
                      <Box>
                        <Typography variant="body2">Calibrate Manager</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                          Read-only access to User Management. Full access to all folders and integrations on the web platform.
                        </Typography>
                      </Box>
                      <Switch
                        size="small"
                        checked={drafts[user?.id ?? CREATE_MODE_DRAFT_KEY]?.permissions?.is_calibrate_manager ?? user?.profile.is_calibrate_manager ?? false}
                        onChange={(e) => {
                          const val = e.target.checked;
                          const draftKey = user?.id ?? CREATE_MODE_DRAFT_KEY;
                          if (val === (user?.profile.is_calibrate_manager ?? false)) {
                            clearDraft(draftKey, "permissions");
                          } else {
                            updateDraft(draftKey, "permissions", { is_calibrate_manager: val });
                          }
                        }}
                        disabled={user?.profile.is_superuser}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </CustomScrollbars>
            {isCreateMode ? (
              <Box px={2} pb={2} pt={1} sx={{ flexShrink: 0 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleCreateClick}
                  disabled={!canEdit || isCreating || isSavingAll || !username.trim() || !firstName.trim() || isEmailFieldInvalid || isUsernameInvalid}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, py: 0.9 }}
                >
                  {isCreating ? "Creating..." : "Create User"}
                </Button>
              </Box>
            ) : (
              user &&
              !user.profile.is_superuser && (
                <Box px={2} pb={2} pt={1} sx={{ flexShrink: 0 }}>
                  {hasAnyChanges && (
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      display="block"
                      sx={{ mb: 1, color: "#ad1457" }}
                    >
                      Unsaved changes
                    </Typography>
                  )}
                  <Tooltip title={!hasAnyChanges ? "No changes to save" : ""}>
                    <span>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleSaveAll}
                        disabled={!canEdit || !hasAnyChanges || isSavingAll || isEmailFieldInvalid || isUsernameInvalid}
                        sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, py: 0.9 }}
                      >
                        {isSavingAll ? "Saving…" : "Save Changes"}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              )
            )}
          </>
        )}

        {tab === "profile" && (
          <>
            <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
              <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                {!isCreateMode && initialPasswordForUser?.userId === user?.id && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: "rgba(46,125,50,0.08)",
                      border: "1px solid rgba(46,125,50,0.2)",
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="caption" fontWeight={700} color="success.dark" textTransform="uppercase" letterSpacing={0.6}>
                        {initialPasswordForUser.label}
                      </Typography>
                      <IconButton size="small" onClick={() => setInitialPasswordForUser(null)}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                    <TextField
                      value={initialPasswordForUser.password}
                      size="small"
                      fullWidth
                      inputProps={{ readOnly: true, style: { fontFamily: "monospace", fontWeight: 700 } }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => navigator.clipboard.writeText(initialPasswordForUser.password)}>
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                )}

                <TextField
                  label="Username"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setUsernameServerError(null);
                  }}
                  size="small"
                  fullWidth
                  required
                  disabled={!canEdit}
                  error={Boolean(usernameHelperText)}
                  helperText={usernameHelperText}
                  sx={{ mb: 1.25, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                />
                <TextField
                  label="Client Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  size="small"
                  fullWidth
                  required
                  disabled={!canEdit}
                  sx={{ mb: 1.25, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                />
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailServerError(null);
                  }}
                  size="small"
                  fullWidth
                  disabled={!canEdit}
                  error={isEmailFieldInvalid}
                  helperText={emailHelperText}
                  sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                />

                {!isCreateMode && user && (
                  <Box
                    sx={{
                      mb: 2,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1.5,
                      bgcolor: "grey.50",
                      border: "1px dashed",
                      borderColor: "divider",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Platform User ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                      {user.id}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ mb: 1.5 }} />

                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  textTransform="uppercase"
                  letterSpacing={0.6}
                  display="block"
                  mb={0.75}
                >
                  Roles
                </Typography>
                <Box>
                  {(
                    [
                      { key: "is_data_storage_user", label: "Data Storage User" },
                      { key: "is_integration_dashboard_user", label: "Integration Dashboard User" },
                      { key: "is_ai_agent_user", label: "AI Agent User" },
                    ] as const
                  ).map(({ key, label }) => (
                    <Box key={key} display="flex" alignItems="center" justifyContent="space-between" py={0.4}>
                      <Typography variant="body2">{label}</Typography>
                      <Switch
                        size="small"
                        checked={roles[key]}
                        onChange={(e) => {
                          if (!canEdit) return;
                          const checked = e.target.checked;
                          setRoles((prev) => {
                            const next = { ...prev, [key]: checked };
                            syncProfileDraft(next, requiresPasswordChange);
                            return next;
                          });
                        }}
                        disabled={!canEdit}
                      />
                    </Box>
                  ))}
                </Box>

                {!isCreateMode && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={700}
                      textTransform="uppercase"
                      letterSpacing={0.6}
                      display="block"
                      mb={0.75}
                    >
                      Security
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="space-between" py={0.4} sx={{ minWidth: 0 }}>
                      <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                        <Typography variant="body2">Require password change</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                          User will be forced to set a new password on next login
                        </Typography>
                      </Box>
                      <Switch
                        size="small"
                        checked={requiresPasswordChange}
                        onChange={(e) => {
                          const val = e.target.checked;
                          setRequiresPasswordChange(val);
                          syncProfileDraft(roles, val);
                        }}
                        disabled={!canEdit && !isManager}
                        sx={{ flexShrink: 0 }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </CustomScrollbars>

            
            <Box px={2} pb={2} pt={1} sx={{ display: "flex", flexDirection: "column", gap: 1, flexShrink: 0 }}>
              {isCreateMode ? (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleCreateClick}
                  disabled={!canEdit || isCreating || isSavingAll || !username.trim() || !firstName.trim() || isEmailFieldInvalid || isUsernameInvalid}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
                >
                  {isCreating ? "Creating..." : "Create User"}
                </Button>
              ) : (
                <>
                  {hasAnyChanges && (
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      display="block"
                      sx={{ mb: 0.5, color: "#ad1457" }}
                    >
                      Unsaved changes
                    </Typography>
                  )}
                  <Tooltip title={!hasAnyChanges ? "No changes to save" : ""}>
                    <span>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => {
                          if (!hasAnyChanges || !user) return;
                          if (isEmailFieldInvalid || isUsernameInvalid) return;
                          const sections = buildSectionsForConfirm();
                          if (sections.length === 0) return;
                          openSaveConfirm(
                            sections,
                            () => { closeSaveConfirm(); executeUnifiedSave(user.id); },
                          );
                        }}
                        disabled={!canEdit || isSaving || isSavingAll || !hasAnyChanges || isEmailFieldInvalid || isUsernameInvalid}
                        sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
                      >
                        {isSaving ? "Saving..." : "Save Changes"}
                      </Button>
                    </span>
                  </Tooltip>
                  <Box sx={{ display: "flex", gap: 1, "& > *": { flex: 1 } }}>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={handleResetPassword}
                      disabled={(!canEdit && !isManager) || isResetting || !user?.email || isCreateMode}
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5, fontSize: 12 }}
                    >
                      Reset Password
                    </Button>
                    <Tooltip title={user?.profile.is_superuser ? "Cannot generate password for Super Admin." : ""}>
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          onClick={handleGeneratePassword}
                          disabled={(!canEdit && !isManager) || isGenerating || !!user?.profile.is_superuser}
                          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5, fontSize: 12 }}
                        >
                          {isGenerating ? "Generating..." : "Generate Password"}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                  {user && !isCreateMode && !user.profile.is_superuser && user.id !== currentUser?.id && (
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      color={user.profile.is_archived ? "success" : "error"}
                      onClick={handleArchive}
                      disabled={!canEdit || isArchiving}
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5, fontSize: 12 }}
                    >
                      {isArchiving
                        ? "Processing..."
                        : user.profile.is_archived
                          ? "Un-Archive User"
                          : "Archive User"}
                    </Button>
                  )}
                </>
              )}
            </Box>
          </>
        )}
      </Box>

      
      <AppDialog
        open={confirm.open}
        onClose={closeConfirm}
        title={confirm.title}
        actions={
          <>
            <Button onClick={closeConfirm} sx={{ textTransform: "none" }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => { closeConfirm(); confirm.onConfirm(); }}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Confirm
            </Button>
          </>
        }
      >
        {confirm.showWarning && (
          <Typography
            variant="subtitle2"
            fontWeight={800}
            sx={{ color: "error.main", textTransform: "uppercase", mb: 1.5 }}
          >
            Warning
          </Typography>
        )}
        <Typography variant="body2">{confirm.message}</Typography>
      </AppDialog>

      
      <AppDialog
        open={saveConfirm.open}
        onClose={closeSaveConfirm}
        title="Confirm Access Changes"
        maxWidth="sm"
        actions={
          <>
            <Button onClick={closeSaveConfirm} sx={{ textTransform: "none" }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => { closeSaveConfirm(); saveConfirm.onConfirm(); }}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Confirm
            </Button>
          </>
        }
      >
        {saveConfirm.sections.map((section, idx) => (
            <Box key={section.label}>
              {idx > 0 && <Divider sx={{ my: 2 }} />}
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                textTransform="uppercase"
                letterSpacing={0.6}
                display="block"
                sx={{ mb: 1 }}
              >
                {section.label}
              </Typography>
              {section.items && section.items.length > 0 && (
                <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}>
                  {section.items.map((item) => (
                    <Typography key={item} variant="body2" component="li">
                      {item}
                    </Typography>
                  ))}
                </Box>
              )}
              {section.added && section.added.length > 0 && (
                <Box sx={{ mb: section.updated?.length || section.enabled?.length || section.disabled?.length || section.removed?.length ? 1.5 : 0 }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    letterSpacing={0.5}
                    sx={{ color: "success.main", display: "block", mb: 0.5 }}
                  >
                    Added
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}>
                    {section.added.map((item) => (
                      <Typography key={item} variant="body2" component="li">
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              {section.updated && section.updated.length > 0 && (
                <Box sx={{ mb: section.enabled?.length || section.disabled?.length || section.removed?.length ? 1.5 : 0 }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    letterSpacing={0.5}
                    sx={{ color: "info.main", display: "block", mb: 0.5 }}
                  >
                    Updated
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}>
                    {section.updated.map((item) => (
                      <Typography key={item} variant="body2" component="li">
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              {section.enabled && section.enabled.length > 0 && (
                <Box sx={{ mb: section.disabled?.length || section.removed?.length ? 1.5 : 0 }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    letterSpacing={0.5}
                    sx={{ color: "success.main", display: "block", mb: 0.5 }}
                  >
                    Enabled
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}>
                    {section.enabled.map((item) => (
                      <Typography key={item} variant="body2" component="li">
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              {section.disabled && section.disabled.length > 0 && (
                <Box sx={{ mb: section.removed?.length ? 1.5 : 0 }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    letterSpacing={0.5}
                    sx={{ color: "error.main", display: "block", mb: 0.5 }}
                  >
                    Disabled
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}>
                    {section.disabled.map((item) => (
                      <Typography key={item} variant="body2" component="li">
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              {section.removed && section.removed.length > 0 && (
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    letterSpacing={0.5}
                    sx={{ color: "error.main", display: "block", mb: 0.5 }}
                  >
                    Removed
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}>
                    {section.removed.map((item) => (
                      <Typography key={item} variant="body2" component="li">
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ))}
      </AppDialog>

      
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ fontWeight: 600 }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
